"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Img = void 0;
var React = require("react");
var PropsTypes = require("prop-types");
var react_deco_1 = require("react-deco");
var observer_1 = require("./observer");
// https://jmperezperez.com/medium-image-progressive-loading-placeholder/
// https://code.fb.com/android/the-technology-behind-preview-photos/
// https://medium.com/front-end-hacking/progressive-image-loading-and-intersectionobserver-d0359b5d90cd
var rAF = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : setTimeout;
var afterAF = function (fn, aFNumber) {
    if (aFNumber === void 0) { aFNumber = 0; }
    rAF(function () { return aFNumber === 0 ? fn() : afterAF(fn, aFNumber - 1); });
};
var wrapperStyle = {
    position: 'relative',
    overflow: 'hidden'
};
var imgStyle = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: 'auto',
    transition: 'opacity 1s linear',
    zIndex: 3,
};
var imgPlaceholderStyle = __assign(__assign({}, imgStyle), { filter: 'blur(50px)', transform: 'scale(1)', zIndex: 2 });
var backgroundStyle = {
    transition: 'opacity 1s linear',
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    zIndex: 1,
};
var defaultBgColor = '#f6f6f6';
function Img(props) {
    var src = props.src, srcSet = props.srcSet, placeholderSrc = props.placeholderSrc, alt = props.alt, sizes = props.sizes;
    var wrapperProps = __assign({}, props);
    delete wrapperProps.src;
    delete wrapperProps.srcSet;
    delete wrapperProps.sizes;
    delete wrapperProps.alt;
    delete wrapperProps.placeholderSrc;
    delete wrapperProps.bgColor;
    delete wrapperProps.aspectRatio;
    delete wrapperProps.loadOnScreen;
    var bgColor = props.bgColor || defaultBgColor;
    return (React.createElement(react_deco_1.Bare, { constructor: constructor, didMount: function (cmp) { return didMount(cmp, props); }, willUnmount: willUnmount, render: function (cmp) {
            return React.createElement("div", __assign({}, wrapperProps, { "data-wrapper": true, style: __assign(__assign({}, wrapperStyle), wrapperProps.style), ref: function (ref) { return attachWrapperElRef(cmp, props, ref); } }),
                React.createElement(react_deco_1.If, { test: (src || srcSet) && cmp.state.imageLoaded, then: function () {
                        return React.createElement("img", { key: 'img', src: src, srcSet: srcSet, sizes: sizes, style: __assign(__assign({}, imgStyle), { opacity: cmp.state.imageReady ? 1 : .1 }), alt: alt });
                    } }),
                React.createElement(react_deco_1.If, { test: placeholderSrc && cmp.state.placeholderLoaded, then: function () {
                        return React.createElement("img", { key: 'placeholder', src: placeholderSrc, style: __assign(__assign({}, imgPlaceholderStyle), { opacity: cmp.state.imageReady ? 0 : .99 }) });
                    } }),
                React.createElement("div", { key: 'background', style: __assign(__assign({}, backgroundStyle), { backgroundColor: bgColor, opacity: (cmp.state.imageReady || cmp.state.placeholderLoaded) ? 0 : .99, paddingBottom: (getPreserverPaddingBottom(Number(props.aspectRatio)) ||
                            getPreserverPaddingBottom(Number(cmp.state.aspectRatio)) ||
                            getPreserverPaddingBottom(.5)) }) }));
        } }));
}
exports.Img = Img;
Img.propTypes = {
    src: PropsTypes.string,
    srcSet: PropsTypes.string,
    sizes: PropsTypes.string,
    alt: PropsTypes.string,
    placeholderSrc: PropsTypes.string,
    bgColor: PropsTypes.string,
    aspectRatio: PropsTypes.number,
    loadOnScreen: PropsTypes.bool
};
function constructor(cmp) {
    cmp.state = {
        placeholderLoaded: false,
        imageLoaded: false,
        imageReady: false,
    };
}
function beginImgLoad(cmp, props) {
    if (props.placeholderSrc) {
        loadImg(props.placeholderSrc, null, null, function (img) {
            cmp.mounted && cmp.setState({
                placeholderLoaded: true,
                aspectRatio: getImageAspectRatio(img)
            });
        });
    }
    loadImg(props.src, props.srcSet, props.sizes, function (img) {
        cmp.mounted && cmp.setState({
            imageLoaded: true,
            aspectRatio: getImageAspectRatio(img),
        }, function () {
            // nothing fancy, just a trick
            // we set imageReady to true after 5 timeframes,
            // to let the browser to render big images
            // and "try" guarantee smooth transition
            // Anyway, this may not work for very big images
            afterAF(function () { return cmp.setState({ imageReady: true }); }, 5);
        });
    });
}
function attachWrapperElRef(cmp, props, ref) {
    if (ref) {
        ref.onIntersection = function () { return beginImgLoad(cmp, props); };
        cmp.domRef = ref;
    }
    else {
        cmp.domRef = null;
    }
}
function didMount(cmp, props) {
    cmp.mounted = true;
    if (props.loadOnScreen) {
        observer_1.observe(cmp.domRef);
    }
    else {
        beginImgLoad(cmp, props);
    }
}
function willUnmount(cmp) {
    cmp.mounted = false;
    cmp.observedElRef = null;
}
function getImageAspectRatio(img) {
    return img.naturalHeight / img.naturalWidth;
}
function getPreserverPaddingBottom(aspectRatio) {
    var ar = Number(aspectRatio);
    return isNaN(ar) ? 0 : aspectRatio * 100 + '%';
}
function loadImg(src, srcSet, sizes, onLoad) {
    var img = new Image();
    img.onload = function () { return onLoad(img); };
    img.src = src;
    if (typeof srcSet === 'string') {
        img.srcset = srcSet;
    }
    if (typeof sizes === 'string') {
        img.sizes = sizes;
    }
}
//# sourceMappingURL=img.js.map